import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import UiCard from 'garaje/components/ui/card';
import type MetricsService from 'garaje/services/metrics';

interface UpsellSplashSignature {
  Args: {
    /**
     * value to use as "cta_id" value in metrics tracking
     */
    ctaId: string;
    /**
     * path to the image to be used in the splash
     */
    imagePath: string;
    /**
     * if false, the "Upgrade to Premium" button is not shown and an <:upgrade-text> block is yielded in its place
     */
    showUpgradeButton?: boolean;
    /**
     * text to show on the Upgrade button, if it's displayed
     */
    upgradeButtonText?: string;
    /**
     * Text to use as title for display and metrics tracking
     */
    title: string;
  };
  Element: HTMLDivElement;
  Blocks: {
    body: [];
    'upgrade-text': [];
  };
}

export default class UpsellSplash extends Component<UpsellSplashSignature> {
  @service declare metrics: MetricsService;

  get upgradeButtonText(): string {
    return this.args.upgradeButtonText || 'Upgrade to Premium →';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get eventProperties() {
    return {
      cta_clickable_type: this.showUpgradeButton ? 'button' : 'none',
      cta_clickable_text: this.showUpgradeButton ? this.upgradeButtonText : null,
      cta_id: this.args.ctaId,
      cta_title: this.args.title,
      cta_type: 'overlay',
      cta_intent: 'upgrade',
      growth_team_project: true,
    };
  }

  get showUpgradeButton(): boolean {
    return this.args.showUpgradeButton ?? true;
  }

  @action
  logUpsellViewed(): void {
    this.metrics.trackEvent('CTA Viewed', this.eventProperties);
  }

  @action
  logUpgradeButtonClicked(): void {
    this.metrics.trackEvent('CTA Clicked', this.eventProperties);
  }

  <template>
    <UiCard class="upsell-splash" {{didInsert this.logUpsellViewed}} ...attributes>
      <img src={{@imagePath}} alt="" class="product-image mx-auto" role="none" />

      <h2 class="my-4 text-center">
        {{@title}}
      </h2>

      <p>
        {{yield to="body"}}
      </p>

      {{#if this.showUpgradeButton}}
        <LinkTo
          @route="billing.product-plans"
          @model="visitors"
          tabindex="-1"
          class="btn btn--brand my-4"
          data-test-upgrade-button
          {{on "click" this.logUpgradeButtonClicked}}
        >
          {{this.upgradeButtonText}}
        </LinkTo>
      {{else}}
        {{yield to="upgrade-text"}}
      {{/if}}
    </UiCard>
  </template>
}
